"use client";

import type { ComponentProps } from "react";
import { createContext, memo, useContext, useEffect, useRef, useState } from "react";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Brain, ChevronDownIcon, Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@deepguide-ai/ui/collapsible";
import {
  BeautifulUIPixelGridLoader,
  TextShimmerLoader,
} from "@/components/magicui/animated-shiny-text";
import { Marker, MarkerContent, MarkerIcon } from "./marker";
import { Response } from "./response";


type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  elapsedDisplay: string;
  finalDuration: number;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
};

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
  /**
   * Externally-persisted reasoning start timestamp (ms epoch). When provided it
   * is authoritative: the live elapsed display and the final duration derive
   * from it rather than from this component's own mount time. This makes the
   * timer reliable even when one stable row represents different reasoning
   * blocks over a turn and so never witnesses a clean streaming→done
   * transition for the latest block. Without it, behavior is unchanged.
   */
  startTime?: number | null;
};

function formatElapsed(ms: number): string {
  const seconds = ms / 1000;
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }
  return `${Math.round(seconds)}s`;
}

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open,
    defaultOpen = false,
    onOpenChange,
    duration: durationProp,
    startTime,
    children,
    ...props
  }: ReasoningProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });

    const [finalDuration, setFinalDuration] = useControllableState({
      prop: durationProp,
      defaultProp: 0,
    });

    const [elapsedDisplay, setElapsedDisplay] = useState("0.0s");
    const startTimeRef = useRef<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      if (isStreaming) {
        // A provided start is authoritative (and follows prop changes when the
        // stable row moves to a newer reasoning block); otherwise fall back to
        // this component's own mount time.
        if (startTime != null) {
          startTimeRef.current = startTime;
        } else if (startTimeRef.current === null) {
          startTimeRef.current = Date.now();
        }
        // Tick every 100ms for live elapsed display
        intervalRef.current = setInterval(() => {
          if (startTimeRef.current !== null) {
            const elapsed = Date.now() - startTimeRef.current;
            setElapsedDisplay(formatElapsed(elapsed));
          }
        }, 100);

        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      } else {
        // Streaming stopped
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        // Fall back to the persisted start so a row that mounts after the
        // reasoning already finished can still report its duration.
        const start = startTimeRef.current ?? startTime ?? null;
        if (start !== null) {
          const elapsed = Date.now() - start;
          setFinalDuration(Math.ceil(elapsed / 1000));
          setElapsedDisplay(formatElapsed(elapsed));
          startTimeRef.current = null;
        }
      }
    }, [isStreaming, setFinalDuration, startTime]);

    const handleOpenChange = (newOpen: boolean) => {
      setIsOpen(newOpen);
    };

    return (
      <ReasoningContext.Provider
        value={{ isStreaming, isOpen, setIsOpen, elapsedDisplay, finalDuration }}
      >
        <Collapsible
          className={cn("not-prose", className)}
          onOpenChange={handleOpenChange}
          open={isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  }
);

export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  /**
   * Optional headline derived from the reasoning text (e.g. the first
   * line of the reasoning summary). When provided, replaces the generic
   * "Thinking …" / "Thought for X seconds" label. During streaming the
   * headline is wrapped in a shimmer effect; after streaming completes
   * the duration is appended as ` · {Ns}`.
   */
  summary?: string | null;
  /**
   * Whether there is reasoning body content to reveal. When false the
   * trigger renders as a static label — no chevron, no hover +/- swap,
   * and clicking is disabled. Defaults to true.
   */
  expandable?: boolean;
};

export const ReasoningTrigger = memo(
  ({ className, children, summary, expandable = true, ...props }: ReasoningTriggerProps) => {
    const { isStreaming, isOpen, elapsedDisplay, finalDuration } = useReasoning();

    const headline = summary?.trim() ?? "";
    const hasHeadline = headline.length > 0;

    // Trailing duration: live elapsed during streaming, fixed seconds when done.
    const durationLabel = isStreaming
      ? elapsedDisplay
      : finalDuration > 0
        ? `${finalDuration}s`
        : "";

    let fallbackLabel: string;
    if (isStreaming) {
      fallbackLabel = `Thinking ${elapsedDisplay}`;
    } else if (finalDuration > 0) {
      fallbackLabel = `Thought for ${finalDuration} seconds`;
    } else {
      fallbackLabel = "Thought for a few seconds";
    }

    return (
      <CollapsibleTrigger
        disabled={!expandable}
        className={cn(
          "group/reasoning inline-flex w-fit max-w-full min-w-0 text-left outline-none",
          isStreaming && !hasHeadline && "min-w-[7.25rem]",
          !expandable && "cursor-default",
          className
        )}
        {...props}
      >
        {children ?? (
          <Marker className="text-foreground transition-colors">
            <MarkerIcon className={cn(isStreaming && "relative h-[15px] w-[15px]")}>
              {isStreaming ? (
                <span
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    expandable && "group-hover/reasoning:hidden"
                  )}
                >
                  <BeautifulUIPixelGridLoader className="shrink-0 text-text-subtle" />
                </span>
              ) : (
                <Brain
                  className={cn(
                    expandable && "group-hover/reasoning:hidden"
                  )}
                />
              )}
              {expandable &&
                (isOpen ? (
                  <Minus
                    className={cn(
                      "hidden text-text-placeholder group-hover/reasoning:block",
                      isStreaming && "absolute inset-0 m-auto"
                    )}
                  />
                ) : (
                  <Plus
                    className={cn(
                      "hidden text-text-placeholder group-hover/reasoning:block",
                      isStreaming && "absolute inset-0 m-auto"
                    )}
                  />
                ))}
            </MarkerIcon>
            {hasHeadline ? (
              <MarkerContent className="flex items-center gap-1.5">
                <span className="text-foreground shrink-0 font-medium">
                  Thinking
                </span>
                {/* Once expanded, collapse to just "Thinking" — the truncated
                    preview + duration are redundant with the full reasoning
                    body shown below. */}
                {!isOpen &&
                  (isStreaming ? (
                    <TextShimmerLoader
                      text={`${headline} · ${durationLabel}`}
                      size="md"
                      className="min-w-0 !text-[13px] truncate tabular-nums"
                    />
                  ) : (
                    <span className="min-w-0 text-[13px] truncate text-text-subtle">
                      {headline}
                      {durationLabel ? ` · ${durationLabel}` : ""}
                    </span>
                  ))}
              </MarkerContent>
            ) : isStreaming ? (
              <TextShimmerLoader
                text={fallbackLabel}
                size="md"
                className="min-w-0 truncate tabular-nums"
              />
            ) : (
              <span className="min-w-0 truncate">{fallbackLabel}</span>
            )}
            {expandable && (
              <ChevronDownIcon
                className={cn(
                  "size-3.5 shrink-0 transition-transform",
                  isOpen ? "rotate-180" : "rotate-0"
                )}
              />
            )}
          </Marker>
        )}
      </CollapsibleTrigger>
    );
  }
);

export type ReasoningContentProps = ComponentProps<
  typeof CollapsibleContent
> & {
  children: string;
};

export const ReasoningContent = memo(
  ({ className, children, ...props }: ReasoningContentProps) => (
    <CollapsibleContent
      className={cn(
        'mt-1.5 text-sm',
        'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-muted-foreground data-[state=closed]:animate-out data-[state=open]:animate-in outline-none',
        className
      )}
      {...props}
    >
      <Response className='grid gap-3 [&>p]:mb-0 [&>p]:text-sm [&>p]:text-[13px]'>{children}</Response>
    </CollapsibleContent>
  )
)

Reasoning.displayName = "Reasoning";
ReasoningTrigger.displayName = "ReasoningTrigger";
ReasoningContent.displayName = "ReasoningContent";
